import type { ReactNode } from 'react';
import { cn } from '../ui/cn.js';
import { ThemeToggle } from '../theme/ThemeToggle.js';

interface AuthLayoutProps {
  children: ReactNode;
  /** Override the centering width — defaults to ~420px panel territory. */
  panelClassName?: string;
  /** `wide` unlocks a wider column for multi-section pages like signup. */
  wide?: boolean;
}

/**
 * Shared shell for unauth'd pages (login/signup/reset/verify). A warm, flat
 * "Soft Industrial" field — a soft radial wash on the app background, a faint
 * dotted grid, and a centered card. The theme toggle lives top-right so
 * logged-out visitors can switch light/dark on any auth surface.
 */
export function AuthLayout({ children, panelClassName, wide }: AuthLayoutProps) {
  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-bg px-4 py-12 text-fg">
      <Backdrop />

      <div className="absolute right-4 top-4 z-20">
        <ThemeToggle />
      </div>

      <div
        className={cn(
          'relative z-10 w-full',
          wide ? 'max-w-[820px]' : 'max-w-[420px]',
          panelClassName,
        )}
      >
        {children}
      </div>

      <FooterMark />
    </div>
  );
}

function Backdrop() {
  return (
    <>
      {/* soft warm wash anchored top-right, matching the mockup's bg-grad */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(1100px 560px at 80% -12%, rgb(var(--c-panel-3)) 0%, transparent 60%)',
        }}
      />
      {/* faint dotted grid — flat, no glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            'radial-gradient(rgb(var(--c-border) / 0.5) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }}
      />
    </>
  );
}

function FooterMark() {
  return (
    <div className="pointer-events-none absolute bottom-4 left-0 right-0 flex justify-center">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
        Commonwealth Trade Authority · Charter Authentication
      </span>
    </div>
  );
}

/**
 * Shared terracotta seal used in auth page headers — a flat accent tile with a
 * white shield glyph, per the Soft Industrial mockup. Tokenized so it reads
 * correctly in both themes (the legacy hex `Logo` does not).
 */
export function AuthSeal({ size = 44 }: { size?: number }) {
  return (
    <div
      className="grid flex-none place-items-center rounded-xl bg-accent text-white shadow-soft-sm"
      style={{ width: size, height: size }}
      aria-hidden
    >
      <svg
        width={size * 0.55}
        height={size * 0.55}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z" />
        <path d="M12 7v10M7.5 9.5l9 5M16.5 9.5l-9 5" />
      </svg>
    </div>
  );
}
